"use client"

import { useCallback, useLayoutEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { normalizeSocialHandle } from "@/utils/social-handle"

/**
 * Sanitize a raw handle string: strip newlines (always single-line), lowercase,
 * and restrict to IG/TikTok-legal characters [a-z0-9._]. Lowercasing also
 * neutralizes iOS autocorrect/autocapitalize, which WebKit does not reliably
 * disable on contentEditable.
 */
export const sanitizeHandle = (raw: string): string =>
    raw
        .replace(/[\r\n]+/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9._]/g, "")

/**
 * True for form-field names that hold an IG/TikTok handle and therefore must use
 * HandleInput (a contentEditable), never a raw <input>. Covers the canonical
 * "username" plus the alternate names some tool defs use.
 */
export const isHandleField = (name: string): boolean =>
    ["username", "handle", "instagram-handle", "instagram-username", "tiktok-handle", "tiktok-username"].includes(name)

export interface HandleInputProps {
    /** Controlled value (the sanitized handle, no leading @). */
    value: string
    /** Fires with the ALREADY-sanitized value on every keystroke / paste / IME end. Callers never re-sanitize. */
    onChange: (value: string) => void
    /** Enter (when not composing) — never inserts a newline. */
    onSubmit?: () => void
    placeholder?: string
    /** Defaults to sanitizeHandle. Pass a custom fn for other charsets. */
    sanitize?: (raw: string) => string
    /** Max characters (IG 30, TikTok 24). Default 30. */
    maxLength?: number
    disabled?: boolean
    /** Focus on mount (e.g. desktop-only via the caller). */
    autoFocus?: boolean
    ariaLabel?: string
    /** Tailwind classes for the editable box. Caller owns chrome (bg, ring, radius, height). */
    className?: string
    /** Tailwind classes for the placeholder overlay span. */
    placeholderClassName?: string
    inputMode?: React.HTMLAttributes<HTMLDivElement>["inputMode"]
    enterKeyHint?: React.HTMLAttributes<HTMLDivElement>["enterKeyHint"]
    id?: string
    /** Blur passthrough (e.g. react-hook-form validation on blur). */
    onBlur?: () => void
}

// Restore the caret to a character offset after a programmatic textContent rewrite
// (writing textContent collapses the caret to position 0).
function setCaretToOffset(el: HTMLElement, offset: number) {
    const node = el.firstChild ?? el
    const len = el.textContent?.length ?? 0
    const off = Math.min(offset, len)
    const sel = window.getSelection()
    const range = document.createRange()
    try {
        range.setStart(node, node.nodeType === Node.TEXT_NODE ? off : 0)
        range.collapse(true)
        sel?.removeAllRanges()
        sel?.addRange(range)
    } catch {
        /* node detached; ignore */
    }
}

// Insert plain, single-line text at the caret (paste handling).
function insertPlainTextAtCaret(text: string) {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    range.deleteContents()
    const node = document.createTextNode(text)
    range.insertNode(node)
    range.setStartAfter(node)
    range.setEndAfter(node)
    sel.removeAllRanges()
    sel.addRange(range)
}

// Current caret character offset from the start of el (single text-node content).
function getCaretOffset(el: HTMLElement): number {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return el.textContent?.length ?? 0
    const range = sel.getRangeAt(0)
    if (!el.contains(range.endContainer)) return el.textContent?.length ?? 0
    const pre = range.cloneRange()
    pre.selectNodeContents(el)
    pre.setEnd(range.endContainer, range.endOffset)
    return pre.toString().length
}

/**
 * Single-line handle/username input rendered as a contentEditable <div> — NOT an
 * <input>. WebKit's iCloud Keychain credential autofill (AutofillElements.cpp)
 * requires an HTMLInputElement, so a div can never be offered the saved-login
 * email. This is the only by-construction-certain way to suppress Safari's
 * "Other User Names for This Website" menu / key icon on a typeable handle field.
 * (Attribute tricks — autocomplete=off, neutral name, data-* — do NOT stop Safari;
 * confirmed in real Safari.) Ref memory: ig-handle-input-no-credential-autofill.
 *
 * Controlled only (value/onChange); onChange always emits the sanitized value.
 * Vertical centering uses symmetric padding + line-height on a BLOCK element (NOT
 * flex — flex parks the caret at the top when empty). Keep the invariant
 * height === paddingY*2 + lineHeight in any className override, or the caret will
 * be off-center / the text will clip.
 */
export default function HandleInput({
    value,
    onChange,
    onSubmit,
    placeholder = "Enter your Instagram username...",
    sanitize = sanitizeHandle,
    maxLength = 30,
    disabled = false,
    autoFocus = false,
    ariaLabel = "Instagram handle",
    className,
    placeholderClassName,
    inputMode = "text",
    enterKeyHint = "go",
    id,
    onBlur
}: HandleInputProps) {
    const editableRef = useRef<HTMLDivElement>(null)
    const composingRef = useRef(false)

    // Mirror controlled value -> DOM ONLY when they diverge (mount, programmatic
    // clear after submit, external setValue/prefill). During typing the DOM already
    // equals value (we read it from there), so this is a no-op and never resets the
    // caret. SSR-safe: the div renders empty (value is never in JSX); this syncs post-mount.
    useLayoutEffect(() => {
        const el = editableRef.current
        if (el && el.textContent !== value) el.textContent = value
    }, [value])

    // Optional focus-on-mount.
    useLayoutEffect(() => {
        if (autoFocus && !disabled) editableRef.current?.focus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Read DOM -> sanitize -> clamp -> restore caret if rewritten -> emit.
    const readAndEmit = useCallback(() => {
        const el = editableRef.current
        if (!el) return
        const original = el.textContent ?? ""
        let text = sanitize(original)
        if (text.length > maxLength) text = text.slice(0, maxLength)
        if (text !== original) {
            // Map the pre-rewrite caret to the sanitized string so mid-string edits
            // (an uppercase/illegal char or an over-limit paste typed in the middle)
            // keep the caret in place instead of jumping to the end. sanitizeHandle
            // is a per-character transform, so the sanitized prefix length IS the new
            // caret offset.
            const rawCaret = getCaretOffset(el)
            const newCaret = Math.min(sanitize(original.slice(0, rawCaret)).length, maxLength)
            el.textContent = text
            setCaretToOffset(el, newCaret)
        }
        onChange(text)
    }, [sanitize, maxLength, onChange])

    const handleInput = useCallback(() => {
        if (composingRef.current) return // never touch the DOM mid-IME-composition
        readAndEmit()
    }, [readAndEmit])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const isComposing = composingRef.current || e.nativeEvent.isComposing || e.keyCode === 229
            if (e.key === "Enter" && !isComposing) {
                e.preventDefault() // never insert a newline; submit instead
                onSubmit?.()
            }
        },
        [onSubmit]
    )

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            e.preventDefault()
            const raw = e.clipboardData.getData("text/plain").replace(/[\r\n]+/g, "")

            // A pasted profile URL must be EXTRACTED, not run through the
            // per-character sanitizer — that is what turned
            // "https://www.instagram.com/foo?igsh=BAR" into the run-together
            // string "httpswww.instagram.comfooigshBAR". Try Instagram first,
            // then TikTok, and fall back to the raw text (which the sanitizer
            // then handles as before) when neither yields a handle.
            const asInstagram = normalizeSocialHandle(raw, "instagram")
            const parsed = asInstagram.ok || asInstagram.reason !== "platform_mismatch" ? asInstagram : normalizeSocialHandle(raw, "tiktok")
            const text = parsed.ok ? parsed.handle : raw

            const cur = editableRef.current?.textContent?.length ?? 0
            // An extracted handle REPLACES the field contents; a plain fragment is
            // still inserted at the caret so mid-string edits keep working.
            if (parsed.ok && parsed.extractedFromUrl && editableRef.current) {
                editableRef.current.textContent = text.slice(0, maxLength)
                readAndEmit()
                return
            }
            insertPlainTextAtCaret(text.slice(0, Math.max(0, maxLength - cur)))
            readAndEmit()
        },
        [maxLength, readAndEmit]
    )

    return (
        <div className="relative w-full">
            {/* Placeholder overlay — driven by state, never DOM :empty (stray-<br> safe). */}
            {value.length === 0 && (
                <span
                    aria-hidden="true"
                    className={cn(
                        "pointer-events-none absolute inset-0 flex select-none items-center px-4 text-[15px] font-normal text-gray-400",
                        placeholderClassName
                    )}
                >
                    {placeholder}
                </span>
            )}
            <div
                ref={editableRef}
                id={id}
                contentEditable={!disabled}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="false"
                aria-label={ariaLabel}
                aria-placeholder={placeholder}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                dir="ltr"
                inputMode={inputMode}
                enterKeyHint={enterKeyHint}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore
                data-lpignore="true"
                data-bwignore
                data-form-type="other"
                data-protonpass-ignore
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={onBlur}
                onCompositionStart={() => {
                    composingRef.current = true
                }}
                onCompositionEnd={() => {
                    composingRef.current = false
                    readAndEmit()
                }}
                className={cn(
                    "block h-11 w-full overflow-x-auto overflow-y-hidden whitespace-nowrap rounded-[14px] border-0 bg-[#f5f5f5] px-4 py-3 text-[15px] font-medium leading-5 tracking-[0.009em] text-gray-900 outline-none focus:bg-white focus:ring-2 focus:ring-inset focus:ring-gray-900 [&::-webkit-scrollbar]:hidden aria-disabled:cursor-not-allowed aria-disabled:bg-gray-100",
                    className
                )}
            />
        </div>
    )
}
